// truck_gate_in/appointments_and_pre_advices_base_controller.js
import { Controller } from '@hotwired/stimulus';
import $ from 'jquery';

export default class AppointmentsAndPreAdvicesBaseController extends Controller {
  static targets = [
    'purposeRows',
    'truck',
  ];

  connect() {
    // If there’s an entity ID (eg. appointment or pre-advice)
    // then fetch from the server
    const entityId = this.element.dataset[this.entityIdKey];
    if (entityId) {
      this.fetchData(entityId)
    } else {
      // Otherwise, try to populate the form from the initialFormData
      // data attribute.
      const inline = this.element.dataset.initialFormData;
      if (inline) {
        try {
          this.populateForm(JSON.parse(inline));
        } catch (e) {
          console.error("Unable to populate form from initialFormData", e);
        }
      }
    }
  }

  get entityIdKey() {
    throw new Error('entityIdKey must be implemented in child class');
  }

  get fetchUrl() {
    throw new Error('fetchUrl must be implemented in child class');
  }

  async fetchData(id) {
    try {
      const response = await fetch(this.fetchUrl(id));
      if (!response.ok) throw new Error('Failed to fetch data');
      const data = await response.json();
      this.populateForm(data);
    } catch (error) {
      console.error(`Error fetching ${this.entityIdKey}:`, error);
    }
  }

  populateForm(data) {
    if (data.truck) {
      this.updateSelect2(this.truckTarget, data.truck.id, data.truck.title, data.truck.selectText);
    }

    data.purposes.forEach((purpose) => {
      this.selectButtonForPurpose(purpose.purpose_type);
      switch (purpose.purpose_type) {
        case "drop_off_empty_container":
          this.fillContainerDropEmpty(purpose);
          break;
        case "drop_off_full_container":
          this.fillContainerDropFull(purpose);
          break;
        case "pick_up_empty_container":
          this.fillContainerPickupEmpty(purpose);
          break;
        case "pick_up_full_container":
          this.fillContainerPickupFull(purpose);
          break;
        case "drop_off_general_cargo":
          this.fillDropGC(purpose);
          break;
        case "pick_up_general_cargo":
          this.fillPickupGC(purpose);
          break;
        case "drop_off_chassis":
          this.fillDropChassis(purpose);
          break;
        case "pick_up_chassis":
          this.fillPickupChassis(purpose);
          break;
      }
    });
  }

  // Shared utility methods (unchanged from your original code)
  selectButtonForPurpose(purposeType) {
    const buttonMap = {
      "drop_off_empty_container": "container_drop_off",
      "drop_off_full_container": "container_drop_off",
      "pick_up_empty_container": "container_pick_up",
      "pick_up_full_container": "container_pick_up",
      "drop_off_general_cargo": "gc_drop_off",
      "pick_up_general_cargo": "gc_pick_up",
      "drop_off_chassis": "chassis_drop_off",
      "pick_up_chassis": "chassis_pick_up",
    };
    const buttonType = buttonMap[purposeType];
    if (!buttonType) {
      console.error(`No button type mapped for purpose type: ${purposeType}`);
      return;
    }
    const buttonSelector = `button[data-form-manager-type-param="${buttonType}"]`;
    const button = this.element.querySelector(buttonSelector);
    if (!button) {
      console.error(`Could not find button for type: ${buttonType}`);
      return;
    }
    button.click();
  }

  getLastPurposePanel() {
    if (!this.hasPurposeRowsTarget) {
      console.error("Could not find purpose rows");
      return null;
    }
    const newPanel = this.purposeRowsTarget.lastElementChild;
    if (!newPanel) {
      console.error("No new panel was added to purposeRows");
      return null;
    }
    return newPanel;
  }

  selectRadio(panel, value) {
    return new Promise((resolve) => {
      const radio = panel.querySelector(`input[type="radio"][value="${value}"]`);
      if (!radio) {
        console.error(`Could not find radio button with value "${value}" in the panel`);
        resolve();
        return;
      }
      radio.click();
      requestAnimationFrame(() => resolve());
    });
  }

  setContainer(panel, cargo, status) {
    if (!cargo) return;
    const containerField = panel.querySelectorAll('[id^="truck_visit_interchanges_attributes_"][id$="_container_id"]');
    if (containerField.length) {
      const selectedCargo = status === 'full' ? containerField[0] : containerField[1];
      this.updateSelect2(`#${selectedCargo.id}`, cargo.id, cargo.display_name || cargo.container_number);
    }
  }

  setChassis(panel, chassis, status) {
    if (!chassis) return;
    const chassisField = panel.querySelectorAll('[id^="truck_visit_interchanges_attributes_"][id$="_chassis_id"]');
    if (chassisField.length) {
      const selectedChassis = status === 'full' ? chassisField[0] : chassisField[1];
      this.updateSelect2(`#${selectedChassis.id}`, chassis.id, chassis.chassis_number);
    }
  }

  setGenset(panel, genset, status) {
    if (!genset) return;
    const gensetFields = panel.querySelectorAll('[id^="truck_visit_interchanges_attributes_"][id$="_genset_id"]');
    if (!gensetFields.length) return;

    const selected = status === 'full' ? gensetFields[0] : gensetFields[1];
    this.updateSelect2(`#${selected.id}`, genset.id, genset.genset_number);
  }

  setExportRelease(panel, release, status) {
    if (!release) return;
    const releaseFields = panel.querySelectorAll('[id^="truck_visit_interchanges_attributes_"][id$="_export_release_id"]');
    if (!releaseFields.length) return;

    // drop-off panels have two (full & empty); pick-up empty has only one
    const selected = releaseFields.length === 1
      ? releaseFields[0]
      : status === 'full' ? releaseFields[0] : releaseFields[1];

    this.updateSelect2(`#${selected.id}`, release.id, release.export_release);
  }

  setBol(panel, bol, status) {
    if (!bol) return;
    if (status === 'breakbulk') {
      const bolField = panel.querySelector(`[id^="truck_visit_breakbulk_deliveries_attributes_"][id$="_bol_segment_id"]`);
      if (bolField) this.updateSelect2(`#${bolField.id}`, bol.id, bol.number);
    } else {
      const bolField = panel.querySelectorAll(`[id^="truck_visit_interchanges_attributes_"][id$="_bol_segment_id"]`);
      if (bolField.length) {
        const selectedBol = status === 'full' ? bolField[0] : bolField[1];
        this.updateSelect2(`#${selectedBol.id}`, bol.id, bol.number);
      }
    }
  }

  setBooking(panel, booking, status) {
    if (!booking) return;
    if (status === 'breakbulk') {
      const bookingField = panel.querySelector(`[id^="truck_visit_dock_receipts_attributes_"][id$="_booking_id"]`);
      if (bookingField) {
        this.updateSelect2(`#${bookingField.id}`, booking.id, booking.booking_number, booking.selectText);
      }
    } else {
      const bookingField = panel.querySelectorAll(`[id^="truck_visit_interchanges_attributes_"][id$="_booking_id"]`);
      let selectedBooking;
      if (bookingField.length == 1) {
        selectedBooking = bookingField[0];
      } else if (bookingField.length == 2) {
        selectedBooking = status === 'full' ? bookingField[0] : bookingField[1];
      }
      this.updateSelect2(`#${selectedBooking.id}`, booking.id, booking.booking_number, booking.selectText);
    }
  }

  setDeliveryOrder(panel, deliveryOrder, status) {
    if (!deliveryOrder) return;
    if (status === 'breakbulk') {
      const deliveryOrderField = panel.querySelector(`[id^="truck_visit_breakbulk_deliveries_attributes_"][id$="_delivery_order_id"]`);
      if (deliveryOrderField) {
        this.updateSelect2(`#${deliveryOrderField.id}`, deliveryOrder.id, deliveryOrder.number);
      }
    } else {
      const deliveryOrderField = panel.querySelectorAll(`[id^="truck_visit_interchanges_attributes_"][id$="_delivery_order_id"]`);
      if (deliveryOrderField.length) {
        const selectedDeliveryOrder = status === 'full' ? deliveryOrderField[0] : deliveryOrderField[1];
        this.updateSelect2(`#${selectedDeliveryOrder.id}`, deliveryOrder.id, deliveryOrder.number);
      }
    }
  }

  fillContainerDropEmpty(purpose) {
    const panel = this.getLastPurposePanel();
    if (!panel) return;
    this.selectRadio(panel, 'empty').then(() => {
      this.setContainer(panel, purpose.cargo, 'empty');
      this.setChassis(panel, purpose.chassis, 'empty');
      this.setGenset(panel, purpose.genset, 'empty');
      this.setExportRelease(panel, purpose.export_release, 'empty');
      this.setSpecialInstructions(panel, purpose.special_instructions, 'empty');
    });
  }

  fillContainerDropFull(purpose) {
    const panel = this.getLastPurposePanel();
    if (!panel) return;
    this.selectRadio(panel, 'full').then(() => {
      const usageButton = panel.querySelector('[id^="truck_visit_interchanges_attributes_"][id$="_usage_import"]');
      if (usageButton) {
        if (purpose.cargo && purpose.cargo.container_usage) {
          const usage = purpose.cargo.container_usage;
          const usagePrefix = usageButton.id.match(/truck_visit_interchanges_attributes_\d+/)[0];
          if (usage === 'import') {
            const usageImport = document.getElementById(`${usagePrefix}_usage_import`);
            if (usageImport) usageImport.click();
            this.setBol(panel, purpose.bol_segment, 'full');
          } else if (usage === 'export') {
            const usageExport = document.getElementById(`${usagePrefix}_usage_export`);
            if (usageExport) usageExport.click();
            this.setBooking(panel, purpose.booking, 'full');
          }
        }
      } else {
        this.setBooking(panel, purpose.booking, 'full');
      }
      this.setContainer(panel, purpose.cargo, 'full');
      this.setChassis(panel, purpose.chassis, 'full');
      this.setGenset(panel, purpose.genset, 'full');
      this.setExportRelease(panel, purpose.export_release, 'full');
      this.setTemperatureLetterPod(panel, purpose.temperature_letter_port_of_discharge);
      this.setTemperatureLetterVgm(panel, purpose.temperature_letter_vgm);
      this.setSpecialInstructions(panel, purpose.special_instructions, 'full');
    });
  }

  fillContainerPickupEmpty(purpose) {
    const panel = this.getLastPurposePanel();
    if (!panel) return;
    this.selectRadio(panel, 'empty').then(() => {
      this.setBooking(panel, purpose.booking, 'empty');
      this.setExportRelease(panel, purpose.export_release, 'empty');
      this.setContainer(panel, purpose.cargo, 'empty');
      this.setSpecialInstructions(panel, purpose.special_instructions, 'empty');
    });
  }

  fillContainerPickupFull(purpose) {
    const panel = this.getLastPurposePanel();
    if (!panel) return;
    this.selectRadio(panel, 'full').then(() => {
      const usageButton = panel.querySelector('[id^="truck_visit_interchanges_attributes_"][id$="_usage_import"]');
      if (usageButton) {
        if (purpose.cargo && purpose.cargo.container_usage) {
          const usage = purpose.cargo.container_usage;
          const usagePrefix = usageButton.id.match(/truck_visit_interchanges_attributes_\d+/)[0];
          if (usage === 'import') {
            const usageImport = document.getElementById(`${usagePrefix}_usage_import`);
            if (usageImport) usageImport.click();
            this.setBol(panel, purpose.bol_segment, 'full');
          } else if (usage === 'export') {
            const usageExport = document.getElementById(`${usagePrefix}_usage_export`);
            if (usageExport) usageExport.click();
            this.setBooking(panel, purpose.booking, 'full');
          }
        }
      } else {
        this.setBol(panel, purpose.bol_segment, 'full');
      }
      this.setDeliveryOrder(panel, purpose.delivery_order, 'full');
      this.setContainer(panel, purpose.cargo, 'full');
      this.setSpecialInstructions(panel, purpose.special_instructions, 'full');
    });
  }

  fillDropGC(purpose) {
    const panel = this.getLastPurposePanel();
    if (!panel) return;
    this.setBooking(panel, purpose.booking, 'breakbulk');
  }

  fillPickupGC(purpose) {
    const panel = this.getLastPurposePanel();
    if (!panel) return;
    this.setBol(panel, purpose.bol_segment, 'breakbulk');
    this.setDeliveryOrder(panel, purpose.delivery_order, 'breakbulk');
  }

  fillDropChassis(purpose) {
    const panel = this.getLastPurposePanel();
    if (!panel) return;
    this.setChassis(panel, purpose.chassis, 'full');
  }

  fillPickupChassis(purpose) {
    const panel = this.getLastPurposePanel();
    if (!panel) return;
    this.setChassis(panel, purpose.chassis, 'full');
  }

  setSpecialInstructions(panel, text, status) {
    if (!text) return;
    const ta = panel.querySelector(
      `textarea[data-truck-gate-in--purpose-rows-target="${status}SpecialInstructions"]`
    );
    if (ta) ta.value = text;
  }

  setTemperatureLetterPod(panel, pod) {
    if (!pod) return;
    const field = panel.querySelector('select[id$="_temperature_letter_port_of_discharge_id"]');
    if (field) this.updateSelect2(`#${field.id}`, pod.id, pod.display_name);
  }

  setTemperatureLetterVgm(panel, vgm) {
    if (!vgm) return;
    const input = panel.querySelector('input[name$="[temperature_letter_vgm]"]');
    if (input) input.value = vgm;
  }

  updateSelect2(selectTarget, value, text, selectText = null) {
    if (!selectTarget || !value || !text) return;
    const option = new Option(selectText || text, value, true, true);
    if (selectText) option.dataset.selectText = selectText;
    $(selectTarget).append(option).trigger('change.select2')
  }
}
